import React from 'react';
import { Box } from '@mui/material';
import { FONT, TourPalette } from '../tourTheme';

// Small building blocks shared by every tour surface. Plain Box + inline style
// objects, mirroring the prototype exactly — MUI variants would re-introduce the
// app theme this surface deliberately steps outside of.

export const Eyebrow = ({ children, p }: { children: React.ReactNode; p: TourPalette }) => (
  <Box
    sx={{
      // Quiet wayfinding: the accent colour says 'label'; no rule, no wide
      // tracking — nothing performs.
      display: 'inline-flex',
      font: `600 11px/1 ${FONT.display}`,
      letterSpacing: '.08em',
      color: p.aText,
      textTransform: 'uppercase'
    }}
  >
    {children}
  </Box>
);

export const H1 = ({ children, big }: { children: React.ReactNode; big?: boolean }) => (
  <Box
    component="h1"
    sx={{
      // Two sizes per role, no fluid tricks — presence comes from weight and
      // room, not from 54px.
      font: {
        xs: big ? `700 28px/1.12 ${FONT.display}` : `700 24px/1.16 ${FONT.display}`,
        md: big ? `700 40px/1.08 ${FONT.display}` : `700 31px/1.12 ${FONT.display}`
      },
      letterSpacing: big ? '-.022em' : '-.018em',
      m: big ? '14px 0 0' : '13px 0 0',
      maxWidth: big ? '21ch' : 'none',
      textWrap: 'balance'
    }}
  >
    {children}
  </Box>
);

export const Lead = ({ children, width = '62ch' }: { children: React.ReactNode; width?: string }) => (
  <Box
    component="p"
    sx={{ font: `400 15.5px/1.65 ${FONT.body}`, opacity: 'var(--tc-ink-2, .72)', m: '12px 0 0', maxWidth: width, textWrap: 'pretty' }}
  >
    {children}
  </Box>
);

// Every interactive element in the tour shares this ring. Only TourButton had
// one before, so the skip / back / docs / dismiss controls were invisible to
// keyboard users.
export const focusRingSx = (accent: string) => ({
  '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 }
});

/** Light containers need more than a hairline against the near-white page —
 *  a soft two-layer shadow gives them an edge; dark mode's tint needs none. */
export const panelShadow = (p: TourPalette) => (p.dark ? 'none' : '0 1px 2px rgba(11,21,18,.04), 0 12px 32px -24px rgba(11,21,18,.22)');

export const StatTile = ({ value, label, p, accent }: { value: React.ReactNode; label: string; p: TourPalette; accent?: boolean }) => (
  <Box
    sx={{
      p: '18px',
      border: `1px solid ${p.line}`,
      borderRadius: '14px',
      background: p.dark ? p.card : p.panel,
      boxShadow: panelShadow(p)
    }}
  >
    <Box sx={{ font: `600 28px/1 ${FONT.mono}`, fontVariantNumeric: 'tabular-nums', color: accent ? p.aText : 'inherit' }}>{value}</Box>
    <Box sx={{ font: `400 12px/1.5 ${FONT.body}`, opacity: 0.65, mt: '8px' }}>{label}</Box>
  </Box>
);

// The one button in the tour. Four hand-rolled copies existed before (top bar,
// app bar, spotlight tip, Discord link) and only this one carried a focus ring,
// so every other control was invisible to keyboard users.
export const TourButton = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  p,
  full,
  href
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost';
  size?: 'sm' | 'md';
  p: TourPalette;
  full?: boolean;
  href?: string;
}) => (
  <Box
    component={href ? 'a' : 'button'}
    type={href ? undefined : 'button'}
    href={href}
    target={href ? '_blank' : undefined}
    rel={href ? 'noopener noreferrer' : undefined}
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      font: `${variant === 'primary' ? 600 : 500} ${size === 'sm' ? 12 : variant === 'primary' ? 14 : 13}px/1.25 ${FONT.body}`,
      padding: size === 'sm' ? '10px 14px' : '14px 22px',
      // Comfortable-target minimum; sm sizes measured 30-34px before.
      minHeight: 44,
      borderRadius: '8px',
      width: full ? '100%' : 'auto',
      // Ghosts are text, not outlined chips — one key per screen is enough.
      border: 'none',
      // A key, not a pill: solid fill with a machined bottom edge, darker
      // solid on hover, one pixel of real travel on press. The 135° gradient
      // and floating glow were the giveaway.
      background: variant === 'primary' ? p.btn : 'transparent',
      color: variant === 'primary' ? `${p.btnInk} !important` : 'inherit',
      boxShadow: variant === 'primary' ? 'inset 0 -2px 0 rgba(0,0,0,.22)' : 'none',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'background-color .15s ease, transform .05s ease, box-shadow .05s ease',
      '&:hover': variant === 'primary' ? { background: p.btnHover } : { background: p.well },
      '&:active':
        variant === 'primary'
          ? { transform: 'translateY(1px)', boxShadow: 'inset 0 -1px 0 rgba(0,0,0,.22)' }
          : { transform: 'translateY(1px)' },
      '@media (prefers-reduced-motion: reduce)': { '&:active': { transform: 'none' } },
      ...focusRingSx(p.accent)
    }}
  >
    {children}
  </Box>
);

// "Read the docs" affordance — every tour page that maps to a documented area
// links out to it, so the tour teaches and the docs go deeper.
export const DocsLink = ({ href, label, p }: { href: string; label: string; p: TourPalette }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      font: `500 12.5px/1 ${FONT.body}`,
      color: `${p.aText} !important`,
      textDecoration: 'none',
      border: `1px solid ${p.line}`,
      borderRadius: '8px',
      p: '10px 13px',
      minHeight: 44,
      boxSizing: 'border-box',
      transition: 'border-color .2s ease, background-color .2s ease',
      '&:hover': { borderColor: p.accent, background: p.softAccent },
      '&:focus-visible': { borderColor: p.accent, background: p.softAccent, outline: `2px solid ${p.accent}`, outlineOffset: 2 }
    }}
  >
    {label}
    {/* `1` on width/height in sx means 100%, not 1px — px strings keep this
        out of the layout instead of stretching the page. */}
    <Box
      component="span"
      sx={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap'
      }}
    >
      (opens in a new tab)
    </Box>
    <Box component="span" aria-hidden sx={{ font: `500 11px/1 ${FONT.mono}` }}>
      ↗
    </Box>
  </Box>
);

export const DocsRow = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: '10px', mt: '22px', flexWrap: 'wrap' }}>{children}</Box>
);
